import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import {
  DEFAULT_NII_SHEET_SELECTION,
  type NiiSheetSelection,
} from 'features/libra/stepComponents/niiReport/sheet/niiSheetSelection'
import { useCallback, useMemo } from 'react'

import type { NiiReportSheetExportPanelData } from './niiReportSheetExportPanelData'

/**
 * The panel's export choices (sheets/columns and whether in-progress tickets
 * are included), read from and written back to saved panel data. Shared by
 * the panel (which only reads it) and the config drawer, so the mapping
 * between panel data and these values lives in one place.
 */
export function useNiiReportSheetSelection(): {
  selection: NiiSheetSelection
  setSelection: (next: NiiSheetSelection) => void
  includeIncomplete: boolean
  setIncludeIncomplete: (next: boolean) => void
} {
  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<NiiReportSheetExportPanelData>()

  const selection = useMemo<NiiSheetSelection>(
    () => ({
      excludedSheets:
        panelData.excludedSheets ?? DEFAULT_NII_SHEET_SELECTION.excludedSheets,
      excludedColumns:
        panelData.excludedColumns ??
        DEFAULT_NII_SHEET_SELECTION.excludedColumns,
    }),
    [panelData.excludedSheets, panelData.excludedColumns]
  )

  const setSelection = useCallback(
    (next: NiiSheetSelection) => {
      setPanelData((prev) => ({
        ...prev,
        excludedSheets: next.excludedSheets,
        excludedColumns: next.excludedColumns,
      }))
    },
    [setPanelData]
  )

  const setIncludeIncomplete = useCallback(
    (next: boolean) => {
      setPanelData((prev) => ({ ...prev, includeIncomplete: next }))
    },
    [setPanelData]
  )

  return {
    selection,
    setSelection,
    includeIncomplete: panelData.includeIncomplete ?? false,
    setIncludeIncomplete,
  }
}
